import Link from "next/link";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import type { ParagraphBlogLatest as ParagraphBlogLatestData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import styles from "./ParagraphBlogLatest.module.css";

/**
 * Layout 67 - `PARAGRAPH_BLOG_LATEST_LAYOUT`: grid 3 kart wpisow blogowych.
 *
 * Struktura:
 *   - Header: eyebrow (text_1) + H2 (name) + lead (text) po lewej,
 *             opcjonalny "Przejdz do bazy wiedzy" CTA po prawej
 *   - Grid 3-kartowy (responsive: 3-2-1 cols)
 *   - Karta: obrazek (16:9) + meta (data) + tytul + excerpt (HTML) + "Czytaj dalej" CTA
 */
export function ParagraphBlogLatest({
  paragraph,
  t,
}: {
  paragraph: ParagraphBlogLatestData;
  t: ParagraphTranslations;
}) {
  if (!Array.isArray(paragraph.items) || paragraph.items.length === 0) return null;

  const eyebrow = (paragraph.text_1 ?? "").trim();
  const heading = paragraph.name;
  const lead = (paragraph.text ?? "").trim();
  const seeAllUrl = paragraph.see_all_url ? extractInternalHref(paragraph.see_all_url) ?? paragraph.see_all_url : null;
  const seeAllLabel = paragraph.see_all_label ?? t.blogLatest.seeAllDefault;
  const tr = t.blogLatest;

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        <div className={styles.headerMain}>
          {eyebrow !== "" ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
          {heading ? <h2 className={styles.heading}>{heading}</h2> : null}
          {lead !== "" ? <p className={styles.lead}>{lead}</p> : null}
        </div>
        {seeAllUrl ? (
          <Link href={seeAllUrl} className={styles.seeAllBtn}>{seeAllLabel}</Link>
        ) : null}
      </div>

      <div className={styles.grid}>
        {paragraph.items.map((item) => {
          const href = extractInternalHref(item.href) ?? item.href;
          const dateLabel = item.publication_label;
          return (
            <Link key={item.id} href={href} className={styles.card}>
              <div className={styles.cardMedia}>
                {item.image ? (
                  <picture>
                    {item.image_phone ? (
                      <source srcSet={item.image_phone} media="(max-width: 480px)" />
                    ) : null}
                    {item.image_tablet ? (
                      <source srcSet={item.image_tablet} media="(max-width: 1024px)" />
                    ) : null}
                    <img src={item.image} alt={item.name} loading="lazy" className={styles.cardImg} />
                  </picture>
                ) : (
                  <span className={styles.cardImgPlaceholder} aria-hidden="true" />
                )}
              </div>
              <div className={styles.cardBody}>
                {dateLabel !== "" ? <p className={styles.cardMeta}>{dateLabel}</p> : null}
                <h3 className={styles.cardTitle}>{item.name}</h3>
                {item.short_text ? (
                  <div
                    className={styles.cardExcerpt}
                    dangerouslySetInnerHTML={{ __html: item.short_text }}
                  />
                ) : null}
                <span className={styles.cardCta}>{tr.readMore}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

